/**
 * Piece-kind registry (spec §2.1.1).
 *
 * QPC's `tariff_type` is a vtable, which makes new tariff shapes additive
 * without touching the search loop. We keep that property but key the handler
 * by a NAME carried in the data, so a PieceSet stays JSON, stays hashable, and
 * survives structuredClone into a Worker. `placements()` is the customisable
 * inner-loop body.
 */

import type {
  Day, Money, Piece, PieceEnvelope, Quantities, WeekdayMask,
} from './types';
import { ALL_WEEKDAYS } from './types';
import { evaluatePrice } from './price';

export interface Placement {
  /** Nights covered, starting at the offered night index. */
  length: number;
  price: Money;
  /**
   * Expansion UNITS this placement leaves consumed, for a `continueWhenBroken`
   * piece — the main block counts as unit 1, each extension step as one more.
   * The engine carries it in the DP label so a later segment resumes the curve
   * instead of restarting it.
   *
   * Units, not steps. With steps, a placement of `steps = 0` leaves the counter
   * at 0, which is indistinguishable from "not yet placed" — so four
   * consecutive one-night placements each restarted as a fresh main block and
   * the run was priced at 4x base instead of F(4).
   */
  units?: number;
}

/** Prices precomputed once per stay, so the hot loop never re-evaluates them. */
export interface PricedPiece {
  main: number;
  step: number;
  end?: number;
  /** True when any component is per-night, so the price varies by placement. */
  dynamic: boolean;
}

export interface KindContext {
  /** Absolute day of night index 0. */
  arrival: Day;
  /** Total nights requested. */
  nights: number;
  qty: Quantities;
  /** Populated by the engine (step 2). Keyed by piece id. */
  priced?: Map<string, PricedPiece>;
  /**
   * Expansion units already consumed per `continueWhenBroken` piece id, taken
   * from the DP label currently being extended. Absent or 0 = a fresh run.
   */
  stepOffset?: Readonly<Record<string, number>>;
}

/** Length-independent unless a per-night component is present. */
export function isDynamic(expr: import('./types').PriceExpr | undefined): boolean {
  if (!expr) return false;
  if (expr.perNight) return true;
  return Boolean(expr.per?.some((c) => c.perNight));
}

export interface PieceKindHandler<P = unknown> {
  kind: string;
  placements(env: PieceEnvelope, params: P, d: number, ctx: KindContext): Placement[];
}

/** 1970-01-01 was a Thursday; weekday 0 = Monday. */
export function weekdayOf(day: Day): number {
  return (((day + 3) % 7) + 7) % 7;
}

export function weekdayAllowed(day: Day, mask: WeekdayMask | undefined): boolean {
  if (mask === undefined || mask === ALL_WEEKDAYS) return true;
  return (mask & (1 << weekdayOf(day))) !== 0;
}

/** A placement must sit wholly inside one window, when windows are declared. */
export function insideWindow(env: PieceEnvelope, from: Day, to: Day): boolean {
  if (!env.windows || env.windows.length === 0) return true;
  return env.windows.some((w) => from >= w.start && to <= w.end);
}

/**
 * How far a placement starting on `day` may run before leaving its window.
 *
 * QPC clamps a piece to its `allowed_period` and charges the FULL piece price
 * for whatever is left — a 3-night `week-tarief` starting the night before its
 * window closes is placed for 1 night at the full €250. Without this clamp the
 * only length offered is the untrimmed one, which fails `insideWindow` and the
 * piece vanishes; a stay whose only other option carries a minimum then has no
 * legal tiling at all and is quoted as unavailable.
 *
 * Returns Infinity when the piece declares no windows.
 */
function windowLimit(env: PieceEnvelope, day: Day): number {
  if (!env.windows || env.windows.length === 0) return Infinity;
  let best = 0;
  for (const w of env.windows) {
    if (day >= w.start && day < w.end && w.end - day > best) best = w.end - day;
  }
  return best;
}

/**
 * Longest placement this piece can make starting at night index `d`.
 *
 * QPC (basic_tariff_get_entry) clamps the offered length to the piece's
 * use_length AND to the remainder of its validity period, then charges the
 * FULL piece price regardless of the resulting length. A piece therefore never
 * runs past its own validity window, and never past the requested departure —
 * but it may be trimmed short and still cost full price. Fixture
 * `14-static-arrangement-short-after` is exactly this case.
 */
function legalLengths(
  env: PieceEnvelope,
  natural: number,
  minNights: number,
  d: number,
  ctx: KindContext,
): number[] {
  const day = ctx.arrival + d;
  if (day < env.validFrom || day >= env.validTo) return EMPTY;
  if (!weekdayAllowed(day, env.arrivalWeekdays)) return EMPTY;

  const maximal = Math.min(ctx.nights - d, natural, env.validTo - day, windowLimit(env, day));
  if (maximal < minNights || maximal < 1) return EMPTY;

  // Fast path. With a length-independent price, the maximal placement weakly
  // dominates every shorter one: same cost, more nights covered. QPC relies on
  // this too (basic_tariff_get_entry returns a single entry).
  const constrained = env.departureWeekdays !== undefined
    || (env.windows !== undefined && env.windows.length > 0);
  if (!constrained) return [maximal];

  // Constrained path. The maximal length may land on an illegal departure
  // weekday or fall outside every window while a SHORTER length is perfectly
  // legal — so returning only the maximal silently drops valid placements.
  // Each candidate length must be validated on its own.
  const out: number[] = [];
  for (let len = maximal; len >= minNights; len--) {
    if (!weekdayAllowed(day + len, env.departureWeekdays)) continue;
    if (!insideWindow(env, day, day + len)) continue;
    out.push(len);
  }
  return out;
}

const EMPTY: number[] = [];

const simpleHandler: PieceKindHandler<{ nights: number; minNights?: number }> = {
  kind: 'simple',
  placements(env, params, d, ctx) {
    const lengths = legalLengths(env, params.nights, params.minNights ?? 1, d, ctx);
    if (lengths.length === 0) return [];
    const pre = ctx.priced?.get(env.id);
    return lengths.map((length) => ({
      length,
      price: pre && !pre.dynamic ? pre.main : evaluatePrice(env.price, ctx.qty, length, d),
    }));
  },
};

/**
 * A closure covers nights at zero cost, so it always wins any tiling it can
 * join — which is the point: its presence in the winning tiling is what makes
 * the quote refuse or fall back to "on request" (spec §2.7). Deliberate, and
 * it looks like a bug if you do not know that.
 */
const closureHandler: PieceKindHandler<Record<string, never>> = {
  kind: 'closure',
  placements(env, _params, d, ctx) {
    return legalLengths(env, 1, 1, d, ctx).map((length) => ({ length, price: 0 }));
  },
};

/**
 * Extendable: a main block plus repeatable tail steps, each emitted as its own
 * placement so the extension count never enters the DP label. Decay follows
 * QPC's expand_tariff.
 */
const extendableHandler: PieceKindHandler<{
  nights: number;
  minNights?: number;
  extension: import('./types').Extension;
}> = {
  kind: 'extendable',
  placements(env, params, d, ctx) {
    const out: Placement[] = [];
    const day = ctx.arrival + d;
    if (day < env.validFrom || day >= env.validTo) return out;
    if (!weekdayAllowed(day, env.arrivalWeekdays)) return out;

    const ext = params.extension;
    const minNights = params.minNights ?? 1;
    const pre = ctx.priced?.get(env.id);
    const base = pre && !pre.dynamic
      ? pre.main : evaluatePrice(env.price, ctx.qty, params.nights, d);
    const stepBase = pre && !pre.dynamic
      ? pre.step : evaluatePrice(ext.price, ctx.qty, ext.nights, d);
    const endPrice = pre && !pre.dynamic
      ? pre.end
      : (ext.decay?.endPrice
        ? evaluatePrice(ext.decay.endPrice, ctx.qty, ext.nights, d) : undefined);

    const maxSteps = ext.maxSteps ?? Math.ceil(ctx.nights / Math.max(ext.nights, 1)) + 1;
    const minSteps = ext.minSteps ?? 0;
    const available = Math.min(ctx.nights - d, env.validTo - day, windowLimit(env, day));

    // RESUMED SEGMENT (QPC continue_when_broken, expand_tariff.c:246-290).
    //
    // The piece already ran earlier in this stay and was interrupted. QPC
    // carries the previous step count and accumulated price in the partition's
    // value group and continues from there: no second main block, and the decay
    // curve picks up where it stopped. Charging the MARGINAL cost
    // F(offset + m) - F(offset) makes that exact and additive, so the DP can
    // keep accumulating cost one placement at a time.
    const units = ext.continueWhenBroken ? (ctx.stepOffset?.[env.id] ?? 0) : 0;
    if (units > 0) {
      // `units` already consumed means `units - 1` steps taken. Charging the
      // marginal cost F(units - 1 + m) - F(units - 1) is exact and additive, so
      // the DP still accumulates one placement at a time.
      const already = extendedPrice(base, stepBase, endPrice, units - 1, ext);
      for (let m = 1; units - 1 + m <= maxSteps; m++) {
        const length = m * ext.nights;
        if (length > available) break;
        if (!weekdayAllowed(day + length, env.departureWeekdays)) continue;
        if (!insideWindow(env, day, day + length)) continue;
        const price = extendedPrice(base, stepBase, endPrice, units - 1 + m, ext) - already;
        out.push({ length, price, units: units + m });
      }
      return out;
    }

    /** Consecutive base placements available here; -1 until first needed. */
    let repeats = -1;

    // The BASE block may overhang and be trimmed at full price (QPC does this;
    // fixture 54 depends on it). EXPANSION steps may not: adding a step that
    // does not fit inflates the price without covering nights, which lets a
    // short stay masquerade as clearing a `min_booking_price` floor it cannot
    // actually reach.
    for (let steps = 0; steps <= maxSteps; steps++) {
      if (steps < minSteps) continue;
      const natural = params.nights + steps * ext.nights;
      if (steps > 0 && natural > available) break;

      // One length per step count, NOT every legal length. `legalLengths`
      // enumerates shorter alternatives, which is right for a `simple` piece
      // (its price is length-independent) but catastrophic here: emitting a
      // 4-unit run as a 1-night placement keeps the 4-unit price, so a short
      // stay appears to clear a `min_booking_price` floor it cannot reach.
      // If this run's own length is illegal, skip the step count rather than
      // shrinking the length under a fixed price.
      const lengths = legalLengths(env, natural, minNights, d, ctx)
        .filter((l) => l === Math.min(natural, available));
      if (lengths.length === 0) {
        if (Math.min(natural, available) < minNights) break;
        continue;
      }
      let price = extendedPrice(base, stepBase, endPrice, steps, ext);
      // Parity rule: a run must never cost more than the same nights expressed
      // as repeated base placements, when that decomposition is legal.
      //
      // With `expand_prices` dearer than `prices` (e.g. 24 vs 23) an expansion
      // run is the MORE expensive way to cover a span. The DP would otherwise
      // keep the dearer variant around and select it to clear a
      // `min_booking_price` floor that the cheapest layout misses — inventing a
      // valid-looking total the wasm can never produce. The engine must price a
      // span at its cheapest legal expression, full stop.
      const span = params.nights + steps * ext.nights;
      if (params.nights > 0 && span % params.nights === 0) {
        if (repeats < 0) repeats = maxRepeatedBase(env, params.nights, ext, day, available);
        if (span / params.nights <= repeats) {
          price = Math.min(price, base * (span / params.nights));
        }
      }
      for (const length of lengths) {
        out.push(ext.continueWhenBroken ? { length, price, units: steps + 1 } : { length, price });
      }
      // Once the natural length is clipped by the stay or the validity window,
      // further steps add price without adding nights.
      if (natural >= available) break;
    }
    return out;
  },
};

/**
 * How many back-to-back base placements this piece can really make from `day`.
 *
 * The parity cap above says a run must never cost more than the same nights
 * expressed as repeated base placements — but only WHEN THAT DECOMPOSITION IS
 * LEGAL, which it stopped checking. Where it is not, the cap invents a price
 * the rate card never offers and undercharges:
 *
 *   nights 7 + one 7-night step, `maxPlacements: 1`   authored 100 + 200 = 300,
 *                                                     charged 2 x 100 = 200
 *   the same rate with `minSteps: 1`                  same 300 -> 200, and the
 *                                                     layout it is priced as is
 *                                                     the one minSteps forbids
 *   nights 3, `arriveOn: ['monday']`, 6 nights        the second block would
 *                                                     start on a Thursday, so
 *                                                     350 was charged as 200
 *
 * Computed ONCE per `placements()` call and reused for every step count, which
 * matters: checked per step count instead, it makes the loop quadratic in the
 * steps, and on a one-night base with a one-night tail — the shape half the
 * production long-stay rates have — that is the hot loop.
 *
 * Note the DP would FIND the repeated-base tiling on its own whenever it is
 * legal, cheapest tiling being the whole rule, so a cap that fires only on a
 * legal decomposition can never be the thing that lowers a quote. It stays the
 * guard it was written to be against a dearer variant surviving into a
 * floor-constrained search.
 */
function maxRepeatedBase(
  env: PieceEnvelope,
  nights: number,
  ext: import('./types').Extension,
  day: Day,
  available: number,
): number {
  // A step count below `minSteps` is not offered at all, so the bare base block
  // is not a placement this piece can make and no repetition of it is legal.
  if ((ext.minSteps ?? 0) > 0) return 1;
  const ceiling = Math.min(
    Math.floor(available / nights),
    env.maxPlacements ?? Number.MAX_SAFE_INTEGER,
  );
  // Fast path, and it is the one that matters: with no weekday rule and no
  // window, every repeat that FITS is legal, and `available` already accounts
  // for the stay, the validity range and the window limit, while `ceiling`
  // accounts for maxPlacements. The production long-stay rates are all this
  // shape, and they are also the shape with a one-night base — where walking
  // the repeats runs once per step and makes the placement pass quadratic.
  // Measured on the 2026 comfort card: 17% slower with the walk, unmeasurable
  // with this.
  if (env.arrivalWeekdays === undefined && env.departureWeekdays === undefined
    && (env.windows === undefined || env.windows.length === 0)) {
    return Math.max(ceiling, 1);
  }
  let k = 0;
  while (k < ceiling) {
    const from = day + k * nights;
    const to = from + nights;
    if (from < env.validFrom || from >= env.validTo || to > env.validTo) break;
    if (!weekdayAllowed(from, env.arrivalWeekdays)) break;
    if (!weekdayAllowed(to, env.departureWeekdays)) break;
    if (!insideWindow(env, from, to)) break;
    k++;
  }
  // A run of exactly one base block is the base block, so the cap is a no-op
  // there whatever the constraints say.
  return Math.max(k, 1);
}

/** QPC expand_tariff price accumulation. */
function extendedPrice(
  base: Money,
  stepBase: Money,
  endPrice: Money | undefined,
  steps: number,
  ext: import('./types').Extension,
): Money {
  if (steps === 0) return base;
  const decay = ext.decay;
  if (!decay) return base + stepBase * steps;

  const startAfter = decay.startAfter ?? 0;
  const startFactor = decay.startFactor ?? 1;
  const factor = decay.factor ?? 1;

  // AN UNREACHABLE ASYMPTOTE DISABLES THE DECAY ENTIRELY.
  //
  // `end_expand_price` is the value the decaying price converges to. When it
  // sits on the wrong side of the step price — an asymptote ABOVE the price
  // with a factor below 1 — it can never be reached, and QPC then charges the
  // flat linear price rather than decaying or clamping. Verified against the
  // wasm on two tariffs that are structurally identical except for this:
  //
  //   laagseizoen-lang-b : step 66.43, asymptote 45 (below)  -> decays exactly
  //                        as factor^(units-7), then pins at 45/unit from above
  //   laagseizoen-lang   : step 35.29, asymptote 40 (above)  -> perfectly
  //                        LINEAR at every length 1..30, neither decayed nor
  //                        clamped up to 40
  //
  // Getting this wrong cost 480 instead of 423 on a 12-night stay. It hid for a
  // long time because the parity rule below independently caps a contiguous run
  // at the repeated-base price, which for these tariffs IS the linear price —
  // so contiguous runs looked correct and only interrupted ones were wrong.
  if (endPrice !== undefined && (factor < 1 ? endPrice >= stepBase : endPrice <= stepBase)) {
    return base + stepBase * steps;
  }

  if (decay.appliesToTotal) {
    // QPC is_total_price_factor (expand_tariff.c:346-373): the factor
    // compounds over the steps and multiplies the running TOTAL, not each
    // step. The main price joins that multiplication when decay starts
    // immediately (startAfter < 1) or explicitly includes it; otherwise it is
    // added afterwards, undiscounted.
    const baseInside = startAfter < 1 || Boolean(decay.includesStart);
    let total = stepBase * steps;
    if (baseInside) total += base;
    if (startAfter < steps) total *= startFactor;
    // The factor compounds only over the units PAST `start_after`, not over
    // every step. `includesStart` decides whether the main block counts as one
    // of those units. Applying it from step one (as an earlier version did)
    // discounts the whole low-season curve and silently underprices every long
    // rental stay.
    const decayed = Math.max(0, steps + (decay.includesStart ? 1 : 0) - startAfter);
    total *= Math.pow(factor, decayed);
    // The asymptote applies only once decay has actually begun — same guard as
    // the per-step branch below, and missed here on the first fix. With
    // `start_after: 7` and an asymptote ABOVE the step price, an unguarded
    // clamp turned a 2-night run from 70.57 into 40 x 2 = 80.
    if (endPrice !== undefined && decayed > 0) {
      const units = steps + (baseInside ? 1 : 0);
      const floorTotal = endPrice * units;
      // endPrice is an asymptote: clamp toward it from whichever side.
      if (factor < 1 ? total < floorTotal : total > floorTotal) total = floorTotal;
    }
    if (!baseInside) total += base;
    return total;
  }

  // Per-step decay: each step past startAfter is scaled again.
  //
  // The endPrice clamp belongs INSIDE the decay guard. `end_expand_price` is
  // the asymptote the decaying price converges to, so it only has meaning once
  // decay has started. Applying it to undecayed steps clamps them to the
  // asymptote immediately — with `start_after: 7` and an asymptote ABOVE the
  // step price, that turned 2 nights at 35.29 into 2 x 40 and made v2 quote 80
  // where the wasm quotes 70.57.
  let total = base;
  let current = stepBase;
  for (let i = 0; i < steps; i++) {
    if (i >= startAfter) {
      current *= startFactor * factor;
      if (endPrice !== undefined) {
        if (factor < 1 ? current < endPrice : current > endPrice) current = endPrice;
      }
    }
    total += current;
  }
  return total;
}

const HANDLERS = new Map<string, PieceKindHandler<never>>();
for (const h of [simpleHandler, closureHandler, extendableHandler]) {
  HANDLERS.set(h.kind, h as PieceKindHandler<never>);
}

export function registerKind(handler: PieceKindHandler<never>): void {
  HANDLERS.set(handler.kind, handler);
}

export function placementsFor(piece: Piece, d: number, ctx: KindContext): Placement[] {
  const handler = HANDLERS.get(piece.kind);
  if (!handler) throw new Error(`unknown piece kind: ${piece.kind}`);
  return handler.placements(piece, piece.params as never, d, ctx);
}
